import { FontAwesomeIcon as Icon } from "@fortawesome/react-fontawesome"
import { AuctionRound, Auction, Vehicle } from "domain-models"
import { hasFlag, make, monthYear, number, vehicleAttribute } from 'filters'
import _ from 'lodash'
import moment from 'moment'
import React from "react"
import Modal from "../../components/Modal"
import Trans from '../../components/Trans'
import Images from '../Auctions/AuctionDetails/VehicleData/Details/Images'
import Attributes from '../Auctions/AuctionDetails/VehicleData/Details/Attributes'
import Condition from '../Auctions/AuctionDetails/VehicleData/Details/Condition'
import Equipment from '../Auctions/AuctionDetails/VehicleData/Details/Equipment'
import Description from '../Auctions/AuctionDetails/VehicleData/Details/Description'
import Documents from '../Auctions/AuctionDetails/VehicleData/Documents'

export type SpeedAuktionRound = AuctionRound & {
  SpeedAuktionStartedOn: string
  SpeedAuktionEndsOn: string
  SpeedAuktionClaimedBy: number | null
  SpeedAuktionClaimedOn: string | null
  auction: Auction
  highest_bid?: { Bid: number }
}

type SpeedAuktionDetailsProps = {
  round: SpeedAuktionRound
  onClose: () => void
  onClaim: (roundId: number) => Promise<void>
  claiming: boolean
}

type SpeedAuktionDetailsState = {
  vehicle: Vehicle | null
}

class SpeedAuktionDetails extends React.Component<SpeedAuktionDetailsProps, SpeedAuktionDetailsState> {
  private countdownInterval: NodeJS.Timeout | null = null

  constructor(props: SpeedAuktionDetailsProps) {
    super(props)

    const subjects = props.round.auction?.subjects || []
    const first = _.first(subjects)
    const vehicle = first?.vehicle || first?.leading || first?.leasing

    this.state = {
      vehicle: vehicle as Vehicle
    }
  }

  componentDidMount() {
    this.countdownInterval = setInterval(() => this.forceUpdate(), 1000)
  }

  componentWillUnmount() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval)
    }
  }

  formatCountdown(endsOn: string): string {
    const end = moment(endsOn)
    const now = moment()
    const diff = end.diff(now)

    if (diff <= 0) {
      return 'Abgelaufen'
    }

    const duration = moment.duration(diff)
    const hours = Math.floor(duration.asHours())
    const minutes = duration.minutes()
    const seconds = duration.seconds()

    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`
    }
    return `${minutes}m ${seconds}s`
  }

  isExpired(): boolean {
    const endsOn = this.props.round.SpeedAuktionEndsOn
    return endsOn ? moment(endsOn).isBefore(moment()) : false
  }

  getPrice(): number | null {
    const round = this.props.round
    // Try highest_bid first (from API), then bids array
    if (round.highest_bid?.Bid) {
      return round.highest_bid.Bid
    }
    // Get max bid from bids array
    if (round.bids && round.bids.length > 0) {
      return Math.max(...round.bids.map(b => b.Bid))
    }
    return null
  }

  handleClaim = async () => {
    await this.props.onClaim(this.props.round.Id)
  }

  render() {
    const { round, onClose, claiming } = this.props
    const { vehicle } = this.state
    const isExpired = this.isExpired()
    const price = this.getPrice()
    const dealer = vehicle?.dealer
    const dealerLocation = dealer
      ? [dealer.FirmExtra, dealer.Street, dealer.PostalCode, dealer.City].filter(Boolean).join(', ')
      : ''

    const body = (
      <div className="text-gray-700 flex flex-col md:flex-row h-full w-full">
        {/* Main Content */}
        <div className="flex-1 min-w-0 overflow-y-auto p-4">
          {/* Header */}
          <div className="flex justify-between items-center mb-4">
            <button onClick={onClose} className="text-gray-600 hover:text-gray-800 flex items-center">
              <Icon icon="arrow-left" className="mr-2" /> <Trans>Zurück</Trans>
            </button>
            <div className="flex items-center">
              <div className="px-3 py-1 bg-yellow-100 border border-yellow-400 rounded mr-4">
                <Icon icon="bolt" className="text-yellow-500" />
                <span className="ml-1 text-yellow-700 font-semibold">Speed-Auktion</span>
              </div>
              <button onClick={onClose} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded">
                <Icon icon="times" className="text-xl" />
              </button>
            </div>
          </div>

          {/* Debug: show vehicle data status */}
          {!vehicle && (
            <div className="mb-4 p-4 bg-yellow-100 border border-yellow-400 text-yellow-700 rounded">
              <Trans>Fahrzeugdaten werden geladen...</Trans>
            </div>
          )}

          {/* Vehicle Images */}
          {vehicle && (
            <div className="mb-4">
              <Images images={vehicle.images || []} />
            </div>
          )}

          {/* Vehicle Title */}
          {vehicle && (
            <div className="mb-4">
              <h2 className="text-2xl font-bold text-gray-800">
                {make(vehicle.Make)} {vehicle.ShortDescription}
              </h2>
              <p className="text-gray-600">{dealerLocation}</p>
            </div>
          )}

          {/* Vehicle Attributes */}
          {vehicle && (
            <div className="mb-4">
              <Attributes vehicle={vehicle} preAuction={false} />
            </div>
          )}

          {/* Condition */}
          {vehicle?.Condition && (
            <div className="mb-4">
              <Condition condition={vehicle.Condition} extra={vehicle.extra} />
            </div>
          )}

          {/* Equipment */}
          {vehicle && (
            <div className="mb-4">
              <Equipment vehicle={vehicle} />
            </div>
          )}

          {/* Description */}
          {vehicle?.LongDescription && (
            <div className="mb-4">
              <Description longDescription={vehicle.LongDescription} />
            </div>
          )}

          {/* Documents (mobile) */}
          <div className="md:hidden mb-4">
            {vehicle?.documents && <Documents documents={vehicle.documents} />}
          </div>
        </div>

        {/* Sidebar */}
        <div className="shrink-0 w-80 border-t md:border-t-0 md:border-l bg-gray-50 p-4 flex flex-col overflow-y-auto">
          {/* Countdown */}
          <div className={`mb-4 p-4 rounded-lg ${isExpired ? 'bg-red-100' : 'bg-yellow-100'}`}>
            <div className="text-sm text-gray-600 mb-1"><Trans>Verbleibende Zeit</Trans></div>
            <div className={`text-2xl font-mono font-bold ${isExpired ? 'text-red-600' : 'text-yellow-600'}`}>
              <Icon icon="clock" className="mr-2" />
              {this.formatCountdown(round.SpeedAuktionEndsOn)}
            </div>
          </div>

          {/* Price */}
          {price && (
            <div className="mb-4 p-4 bg-green-100 rounded-lg">
              <div className="text-sm text-gray-600 mb-1"><Trans>Preis</Trans></div>
              {(() => {
                // Check if vehicle is vatable (SellFlags bit 32768 = MwSt. ausweisbar)
                const isVatable = vehicle && (vehicle.SellFlags & 32768) !== 0
                // Calculate net price if vatable (divide by 1.19)
                const displayPrice = isVatable ? price / 1.19 : price
                // Determine VAT label
                const vatLabel = isVatable
                  ? 'Price netto / exclusive VAT'
                  : '§25 VAT not deductible'

                return (
                  <>
                    <div className="text-3xl font-bold text-green-600">
                      {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(displayPrice)}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      {vatLabel}
                    </div>
                  </>
                )
              })()}
            </div>
          )}

          {/* Vehicle Quick Info */}
          {vehicle && (
            <div className="mb-4 p-3 bg-white rounded-lg border">
              <div className="grid grid-cols-2 gap-2 text-sm">
                {vehicle.Vin
                  ? <div className="col-span-2">
                    <span className="text-gray-500">VIN</span>
                    <div className="text-xl font-bold text-gray-800 select-all cursor-text break-all">{vehicle.Vin}</div>
                  </div>
                  : null
                }
                <div>
                  <span className="text-gray-500"><Trans>Kilometer</Trans></span>
                  <div className="font-medium">{number(vehicle.Kilometers, 0, 'km')}</div>
                </div>
                <div>
                  <span className="text-gray-500"><Trans>EZ</Trans></span>
                  <div className="font-medium">{monthYear(vehicle.RegistrationDate)}</div>
                </div>
                <div>
                  <span className="text-gray-500"><Trans>Leistung</Trans></span>
                  <div className="font-medium">{number(vehicle.Power, 0, 'kW')}</div>
                </div>
                <div>
                  <span className="text-gray-500"><Trans>Kraftstoff</Trans></span>
                  <div className="font-medium"><Trans>{vehicleAttribute('Fuel', vehicle.Fuel)}</Trans></div>
                </div>
                {vehicle.envkv?.EmissionsCombined
                  ? <div>
                    <span className="text-gray-500">CO₂ WLTP komb.</span>
                    <div className="font-medium">{number(vehicle.envkv.EmissionsCombined, 0, 'g/km')}</div>
                  </div>
                  : null
                }
                {vehicle.envkv?.EmissionsCombinedWeighted
                  ? <div>
                    <span className="text-gray-500">CO₂ WLTP gew.</span>
                    <div className="font-medium">{number(vehicle.envkv.EmissionsCombinedWeighted, 0, 'g/km')}</div>
                  </div>
                  : null
                }
                {vehicle.Weight
                  ? <div>
                    <span className="text-gray-500"><Trans>Gewicht</Trans></span>
                    <div className="font-medium">{number(vehicle.Weight, 0, 'kg')}</div>
                  </div>
                  : null
                }
              </div>
            </div>
          )}

          {/* Documents (desktop) */}
          <div className="hidden md:block mb-4">
            {vehicle?.documents && <Documents documents={vehicle.documents} />}
          </div>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Claim Button */}
          <button
            onClick={this.handleClaim}
            disabled={claiming || isExpired}
            className={`w-full py-4 rounded-lg font-bold text-lg transition-colors ${isExpired
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : claiming
                  ? 'bg-blue-400 text-white cursor-wait'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
          >
            {claiming ? (
              <><Icon icon="spinner" spin /> <Trans>Wird beansprucht...</Trans></>
            ) : isExpired ? (
              <Trans>Abgelaufen</Trans>
            ) : (
              <><Icon icon="hand-paper" /> <Trans>Sofort Kauf</Trans></>
            )}
          </button>
        </div>
      </div>
    )

    return (
      <Modal body={body} rounded={true} pin={true} maxWidth="6xl" bodyStyle="overflow-hidden" classes=" w-full" />
    )
  }
}

export default SpeedAuktionDetails
